<template>
  <div :class="[$style.messageItem, isOutgoing ? $style.outgoingRow : $style.incomingRow]">
    <!-- 프로필 이미지 -->
    <div v-if="!isOutgoing && showAvatarAndName" :class="$style.avatarContainer">
      <img :alt="authorName" :src="authorProfileImage || avatarPlaceholder" :class="$style.avatarImage" />
    </div>
    <!-- 아바타 없을 때 공간 유지 -->
    <div v-else-if="!isOutgoing && !showAvatarAndName" :class="$style.avatarPlaceholder" />

    <!-- 메시지 내용 -->
    <div :class="[$style.messageContentWrapper, isOutgoing ? $style.itemsEnd : $style.itemsStart]">
      <!-- 발신자 이름 -->
      <div v-if="!isOutgoing && showAvatarAndName" :class="$style.senderName">
        {{ authorName }}
      </div>

      <!-- 메시지 버블 -->
      <div
        :class="[
          $style.messageBubble,
          isOutgoing ? $style.userMessage : $style.botMessage,
          messageStatusClass,
          message.type !== 'text' ? $style.noBackground : '',
        ]"
      >
        <!-- 텍스트 메시지 -->
        <div
          v-if="message.type === 'text'"
          :class="$style.textBlock"
          v-html="formatTextWithLineBreaks(message.text)"
        ></div>

        <!-- 이미지 메시지 -->
        <div v-else-if="message.type === 'image'" :class="$style.imageBlock">
          <img
            :src="message.uri"
            alt="이미지"
            :class="$style.imageContent"
            loading="lazy"
            @click="openImagePreview(message.uri)"
          />
        </div>

        <!-- 파일 메시지 -->
        <div v-else-if="message.type === 'file'" :class="$style.fileBlock">
          <!-- 비디오 파일 -->
          <div v-if="message.mimeType?.startsWith('video/')">
            <video :class="$style.videoContent" controls preload="metadata">
              <source :src="message.uri" :type="message.mimeType" />
              브라우저에서 비디오 태그를 지원하지 않습니다.
            </video>
            <div :class="$style.fileInfo">{{ message.name }} ({{ formatFileSize(message.size) }})</div>
          </div>
          <!-- 일반 파일 -->
          <div v-else :class="$style.generalFileContainer">
            <component :is="getFileIcon(message.mimeType)" :class="$style.fileIcon" />
            <div :class="$style.fileDetails">
              <div :class="$style.fileName">{{ message.name }}</div>
              <div :class="$style.fileSize">{{ formatFileSize(message.size) }}</div>
            </div>
            <a
              :href="message.uri"
              :download="message.name"
              target="_blank"
              rel="noopener noreferrer"
              :class="$style.downloadLink"
              aria-label="파일 다운로드"
            >
              <ArrowDownTrayIcon :class="$style.downloadIcon" />
            </a>
          </div>
        </div>

        <!-- 지원하지 않는 메시지 -->
        <div v-else :class="$style.unsupportedBlock">
          <span v-if="message.type === 'internal_note'"></span>
          <span v-else>지원하지 않는 메시지 타입입니다.</span>
        </div>
      </div>

      <!-- 메시지 시간 및 상태 표시 -->
      <div v-if="showTime" :class="[$style.messageInfo, isOutgoing ? $style.outgoingTime : $style.incomingTime]">
        <span v-if="isOutgoing" :class="$style.statusIconContainer">
          <ArrowPathIcon v-if="message.status === 'sending'" :class="$style.statusIconSending" />
          <button
            v-else-if="message.status === 'error'"
            :class="$style.retryButton"
            @click="$emit('retry', message.id)"
          >
            <ArrowPathIcon :class="$style.retryIcon" /> 재시도
          </button>
        </span>
        <span>{{ formatMessageTime(message.createdAt) }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, type Component, useCssModule } from "vue"
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  DocumentIcon,
  PhotoIcon,
  VideoCameraIcon,
  MusicalNoteIcon,
  DocumentTextIcon,
  ArchiveBoxIcon,
} from "@heroicons/vue/24/outline"
import { getDateFromTimestamp } from "../utils/chat"
import type { TicketMessageUnion, PersonSnapshot } from "@wowpress/types"
import { useSetting } from "@/composables/useSetting"

const { chatbot } = useSetting()

// --- Props 정의 ---
const props = defineProps<{
  message: TicketMessageUnion
  isOutgoing: boolean
  showTime: boolean
  showAvatarAndName: boolean
}>()

// --- Emits 정의 ---
const emit = defineEmits(["retry"])

// --- CSS Module ---
const $style = useCssModule()

// --- Computed 속성 ---
const author = computed<PersonSnapshot>(() => {
  if (props.message.author.type === "bot") {
    return {
      ...props.message.author,
      name: chatbot.value?.name,
      image: chatbot.value?.avatarUrl,
    } as PersonSnapshot
  }
  return props.message.author ?? { id: "unknown", type: "customer", name: "알 수 없음" }
})
const authorName = computed(() => {
  if (author.value.type === "agent") {
    return "상담사"
  }

  return author.value.name || "알 수 없는 사용자"
})
const authorProfileImage = computed(() => author.value.image)
const avatarPlaceholder = computed(() => {
  const nameInitial = authorName.value?.[0] || "U"
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(nameInitial)}&background=random`
})
// 메시지 상태에 따른 클래스 계산
const messageStatusClass = computed(() => {
  if (props.message.status === "sending") return $style.sending
  if (props.message.status === "error") return $style.error
  return ""
})

// --- Helper 함수 ---
const formatMessageTime = (timestamp: any): string => {
  const date = getDateFromTimestamp(timestamp)
  if (!date) return ""
  const hours = date.getHours().toString().padStart(2, "0")
  const minutes = date.getMinutes().toString().padStart(2, "0")
  return `${hours}:${minutes}`
}
const getFileIcon = (mimeType?: string): Component => {
  if (!mimeType) return DocumentIcon
  if (mimeType.startsWith("image/")) return PhotoIcon
  if (mimeType.startsWith("video/")) return VideoCameraIcon
  if (mimeType.startsWith("audio/")) return MusicalNoteIcon
  if (mimeType === "application/pdf") return DocumentTextIcon
  if (mimeType.includes("zip") || mimeType.includes("archive")) return ArchiveBoxIcon
  return DocumentIcon
}
const formatFileSize = (bytes?: number): string => {
  if (bytes === undefined || bytes === null || bytes < 0) return "0 B"
  if (bytes < 1024) return bytes + " B"
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB"
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + " GB"
}
const openImagePreview = (imageUrl: string) => {
  console.log("Open image preview:", imageUrl)
  window.open(imageUrl, "_blank")
}
const formatTextWithLineBreaks = (text: string | undefined) => {
  if (!text) return ""
  const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  return escaped.replace(/\n/g, "<br>")
}
</script>

<style module>
/* === Layout === */
.messageItem {
  display: flex;
  margin-bottom: 8px;
  /* mb-2 */
}

.outgoingRow {
  flex-direction: row-reverse;
}

.incomingRow {
  flex-direction: row;
}

.avatarContainer {
  flex-shrink: 0;
  margin-right: 10px;
}

.avatarImage {
  width: 32px;
  /* w-8 */
  height: 32px;
  /* h-8 */
  border-radius: 9999px;
  /* rounded-full */
  object-fit: cover;
}

.avatarPlaceholder {
  flex-shrink: 0;
  width: 32px;
  /* w-8 */
  height: 32px;
  /* h-8 */
  margin-right: 10px;
  /* mr-2.5 */
}

.messageContentWrapper {
  display: flex;
  flex-direction: column;
  max-width: 80%;
  box-sizing: border-box;
}

.itemsEnd {
  /* isOutgoing */
  align-items: flex-end;
}

.itemsStart {
  /* !isOutgoing */
  align-items: flex-start;
}

.senderName {
  margin-top: 2px;
  font-size: 0.75rem;
  /* text-xs */
  color: #4b5563;
  /* text-gray-600 */
  margin-bottom: 4px;
  /* mb-1 */
}

/* === Message Bubble === */
.messageBubble {
  background-color: transparent;
  border-radius: 8px;
  /* 기본 텍스트 스타일 */
  max-width: 100%;
  font-size: 14px;
  /* text-sm */
  line-height: 1.625;
  /* leading-relaxed */
  white-space: pre-wrap;
  /* whitespace-pre-wrap */
  color: #111827;
  /* text-gray-900 - Default text color */
  overflow: hidden;
  box-sizing: border-box;
}

.messageBubble.noBackground {
  background-color: transparent;
}

.userMessage {
  /* isOutgoing */
  /* color: #111827; 기본값 사용 */
  background-color: #f9fafb;
  border-bottom-right-radius: 0px;
  /* rounded-br-none */
}

.botMessage {
  /* px-4 py-3 */
  background-color: #ffecf6;
  /* bg-gray-50 */
  /* color: #111827; 기본값 사용 */
  border-top-left-radius: 0px;
  /* rounded-bl-none */
}

.sending {
  /* Opacity for sending status */
  opacity: 0.75;
}

.error {
  /* Border for error status */
  border: 1px solid #fca5a5;
  /* border border-red-300 */
}

/* === Message Content Types === */
.textBlock {
  overflow: hidden;

  /* Styles inherited from .messageBubble */
  padding: 12px 16px;

  /* bg-pink-50 */
}

.imageBlock {
  border-radius: 8px;
  /* rounded-lg */
  overflow: hidden;
  max-width: 200px;
  border: 1px solid #e5e7eb;
  height: 80px;
}

.imageContent {
  display: block;
  /* Prevents extra space below image */
  height: 100%;
  width: auto;
  object-fit: cover;
  cursor: pointer;
  border-radius: 8px;
}

.fileBlock {
  /* Container for file types */
  border-radius: 8px;
  /* rounded */
  border: 1px solid #e5e7eb;
  /* border border-gray-200 */
  overflow: hidden;
  padding: 8px;
  /* p-2 */
  background-color: #ffffff;
  /* bg-white */
}

.videoContent {
  display: block;
  /* Prevents extra space */
  width: 100%;
  border-radius: 8px;
  /* rounded-lg */
  max-width: 200px;
}

.fileInfo {
  margin-top: 4px;
  /* mt-1 */
  font-size: 0.75rem;
  /* text-xs */
  color: #6b7280;
  /* text-gray-500 */
}

.generalFileContainer {
  display: flex;
  align-items: center;
}

.fileIcon {
  margin-right: 8px;
  /* mr-2 */
  height: 20px;
  /* h-5 */
  width: 20px;
  /* w-5 */
  color: #6b7280;
  /* text-gray-500 */
  flex-shrink: 0;
}

.fileDetails {
  flex: 1 1 0%;
  /* flex-1 */
  min-width: 0;
}

.fileName {
  font-weight: 500;
  /* font-medium */
  font-size: 0.875rem;
  /* text-sm */
  color: #111827;
  /* text-gray-900 */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* truncate */
}

.fileSize {
  font-size: 0.75rem;
  /* text-xs */
  color: #6b7280;
  /* text-gray-500 */
}

.downloadLink {
  margin-left: 8px;
  /* ml-2 */
  flex-shrink: 0;
  color: #6b7280;
  /* text-gray-500 */
  display: block;
  /* Ensure it's clickable */
}

.downloadLink:hover {
  color: #374151;
  /* hover:text-gray-700 */
}

.downloadIcon {
  display: block;
  /* Ensure icon size is applied */
  height: 20px;
  /* h-5 */
  width: 20px;
  /* w-5 */
}

.unsupportedBlock {
  font-size: 0.75rem;
  /* text-xs */
  font-style: italic;
  color: #9ca3af;
  /* text-gray-400 */
}

/* === Message Info (Time & Status) === */
.messageInfo {
  font-size: 0.75rem;
  /* text-xs */
  color: #6b7280;
  /* text-gray-500 */
  margin-top: 4px;
  /* mt-1 */
  display: flex;
  align-items: center;
}

.outgoingTime {
  justify-content: flex-end;
  padding-right: 4px;
  /* pr-1 */
}

.incomingTime {
  justify-content: flex-start;
  padding-left: 4px;
  /* pl-1 */
}

.statusIconContainer {
  margin-right: 4px;
  /* mr-1 */
  display: inline-flex;
  /* Align items */
  align-items: center;
}

.statusIconSending {
  display: inline-block;
  color: #9ca3af;
  /* text-gray-400 */
  width: 0.75rem;
  /* w-3 */
  height: 0.75rem;
  /* h-3 */
  animation: spin 1s linear infinite;
}

.retryButton {
  margin-left: 4px;
  /* ml-1 */
  color: #dc2626;
  /* text-red-600 */
  font-size: 0.75rem;
  /* text-xs */
  font-weight: 500;
  /* font-medium */
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.retryButton:hover {
  color: #b91c1c;
  /* hover:text-red-700 */
}

.retryIcon {
  display: inline-block;
  /* Ensure size is applied */
  width: 0.75rem;
  /* w-3 */
  height: 0.75rem;
  /* h-3 */
  margin-right: 2px;
  /* mr-0.5 */
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
